// DraftBodyView — renders the active brief revision.
// AI revisions are shown read-only as paragraphs.
// User-edit revisions open the BriefEditor with debounced auto-save.
//
// Autosave correctness: every queued edit is bound to the draft (and the
// onChange callback) that was current when the user typed it. When the user
// switches drafts (or unmounts the canvas) before the debounce fires, we
// flush the pending edit synchronously against its ORIGINAL onChange, so
// edits to Draft A can never bleed into Draft B.
import { useCallback, useEffect, useMemo, useRef } from "react";
import { BriefEditor } from "./BriefEditor";
import type { BriefEditorContextValue } from "./briefEditorContext";
import type { ApprovedBriefToolbarControl } from "./BriefEditorToolbar";
import type { ContentBriefDraft } from "@/lib/composerCore";
import { markdownToTiptapDoc } from "@/lib/markdownToTiptap";

type DraftBodyViewProps = {
  draft: ContentBriefDraft;
  workspaceId: string;
  briefId: string;
  onChange: (doc: unknown) => void;
  /**
   * Extra editor-context callbacks supplied by the parent (Composer). These
   * are merged into the BriefEditorContext alongside the locally-computed
   * `flushPendingSave`. Used by node-views (e.g. footnote source-attach) to
   * ask the page to refresh after a DB write.
   */
  contextExtras?: Pick<BriefEditorContextValue, "notifyChanged">;
  /** Optional Approved-Brief control forwarded to the BriefEditor toolbar. */
  approvedBriefControl?: ApprovedBriefToolbarControl;
};

export const SAVE_DEBOUNCE_MS = 1500;

type Pending = {
  draftId: string;
  doc: unknown;
  onChange: (doc: unknown) => void;
};

export const DraftBodyView = ({
  draft,
  workspaceId,
  briefId,
  onChange,
  contextExtras,
  approvedBriefControl,
}: DraftBodyViewProps) => {
  const timer = useRef<number | null>(null);
  const pendingRef = useRef<Pending | null>(null);

  const flushPending = useCallback(async () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) await pending.onChange(pending.doc);
  }, []);

  const ctx: BriefEditorContextValue = useMemo(
    () => ({
      workspaceId,
      briefId,
      references: [],
      flushPendingSave: flushPending,
      notifyChanged: contextExtras?.notifyChanged,
    }),
    [workspaceId, briefId, flushPending, contextExtras?.notifyChanged],
  );

  // If the active draft changes while a save is queued, flush against the
  // ORIGINAL draft's onChange before the new draft takes over.
  const prevDraftIdRef = useRef(draft.id);
  useEffect(() => {
    if (prevDraftIdRef.current !== draft.id) {
      const pending = pendingRef.current;
      if (pending && pending.draftId === prevDraftIdRef.current) {
        void flushPending();
      }
      prevDraftIdRef.current = draft.id;
    }
  }, [draft.id, flushPending]);

  // On unmount, flush any in-flight save so we don't lose the user's last edit.
  useEffect(() => {
    return () => {
      void flushPending();
    };
  }, [flushPending]);

  const handleEditorChange = useCallback(
    (json: unknown) => {
      // Capture the draft + onChange that were current at edit time.
      pendingRef.current = { draftId: draft.id, doc: json, onChange };
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (pending) pending.onChange(pending.doc);
      }, SAVE_DEBOUNCE_MS);
    },
    [draft.id, onChange],
  );

  if (draft.revision_kind === "user_edit") {
    return (
      <BriefEditor
        key={draft.id}
        value={draft.draft_doc ?? { type: "doc", content: [{ type: "paragraph" }] }}
        onChange={handleEditorChange}
        context={ctx}
        placeholder="Refine your draft — add footnotes, endnotes, bibliography…"
        workspaceId={workspaceId}
        approvedBriefControl={approvedBriefControl}
      />
    );
  }

  const readOnlyDoc = draft.draft_doc ?? markdownToTiptapDoc(draft.draft_body ?? "No draft body returned.");

  // AI / legacy: render through the same TipTap surface in read-only mode so
  // presentation-only decorations like `[ref:kind:id]` chips can run.
  return (
    <BriefEditor
      key={draft.id}
      value={readOnlyDoc}
      onChange={() => {
        /* read-only AI drafts do not autosave */
      }}
      context={ctx}
      placeholder="No draft body returned."
      editable={false}
      showToolbar={false}
      workspaceId={workspaceId}
      approvedBriefControl={approvedBriefControl}
    />
  );
};
